/**
 * The time a user action is stamped or decided with: always the server's
 * clock, never the device's, which can be months off (pai1 had puzzle solves
 * stamped in April 2027).
 */

/** How long an action waits for the server clock before giving up. */
export const SERVER_TIME_WAIT_MS = 10_000;

/**
 * The server's current time for stamping or deciding an action.
 *
 * @param params - Inputs.
 * @param params.getServerTimeMs - The server time now, or undefined while
 *     the server clock is unknown (TimeContext).
 * @param params.waitForServerTimeMs - Resolves once the server clock is
 *     known. Omitted: no waiting.
 * @param params.waitMs - How long to wait. Omitted:
 *     {@link SERVER_TIME_WAIT_MS}.
 * @return Epoch ms, or undefined when the server clock stays unknown for
 *     the whole wait; the caller then records nothing.
 * @throws Never.
 */
export async function serverActionTimeMs({
  getServerTimeMs,
  waitForServerTimeMs,
  waitMs = SERVER_TIME_WAIT_MS,
}: {
  getServerTimeMs: (() => number | undefined) | undefined;
  waitForServerTimeMs?: () => Promise<number>;
  waitMs?: number;
}): Promise<number | undefined> {
  const known = getServerTimeMs?.();
  if (known !== undefined && Number.isFinite(known)) {
    return known;
  }
  if (waitForServerTimeMs === undefined) {
    return undefined;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<undefined>(settle => {
    timer = setTimeout(() => settle(undefined), waitMs);
  });
  try {
    return await Promise.race([waitForServerTimeMs(), timeout]);
  } finally {
    clearTimeout(timer);
  }
}
